import axios from 'axios';
import qs from 'qs';
import { Message } from 'element-ui';
import { getToken, setToken } from '@/utils/auth';
import { getAccount, removeAccount } from '@/utils/account';
import store from '@/store';
import router from '@/router';
import { API_BASE, getMseTag } from '@/config';
import { showFullScreenLoading, hideFullScreenLoading } from './request-helper';

const { CancelToken } = axios;
let cancel; // 取消请求对象
const promiseArr = {}; // 正在发起请求队列
// const TIME_OUT = 50000; // 超时设置
// 跳转登录页面 code
const jumpLoginCodeList = ['401', '5001'];
// token异常 ：8015 = token为空; 8016=请求token不能为空; 8017=请求token校验失败; 8018=请求token失效
const tokenErrCodeList = ['8015', '8016', '8017', '8018'];

const request = (defaults = {}) => {
  // create an axios instance
  const instance = axios.create({
    headers: {
      'Content-Type': defaults.contentType || 'application/json;charset=utf-8',
    },
    // timeout: defaults.timeout || TIME_OUT,
    cache: false, // Whether to cache
    // withCredentials: true, // send cookies when cross-domain requests
  });

  // request interceptor
  instance.interceptors.request.use(
    (config) => {
      // 显示 loading
      if (defaults.loading) {
        const message = defaults.loadingMessage;
        showFullScreenLoading(message);
      }

      // 处理完整的 URL. 非 http, https 的才处理
      const isExternal = /^(https?:)/.test(config.url);
      if (!isExternal) {
        const hasServer = !!API_BASE[defaults.server];
        if (!hasServer) {
          console.warn(`API_BASE not found 'server: ${defaults.server}' config, will reset server to 'base'`);
          defaults.server = 'base';
        }
        config.url = `${API_BASE[defaults.server]}${config.url}`;
      }

      // 发起请求时，取消掉当前正在进行的相同请求//
      let promiseArrKey = config.url;
      const _data = { ...config.data };
      delete _data._;
      const _params = { ...config.params };
      delete _params._;

      if (JSON.stringify(_data) !== '{}') {
        promiseArrKey += `&${JSON.stringify(_data)}`;
      }
      if (JSON.stringify(_params) !== '{}') {
        promiseArrKey += `&${JSON.stringify(_params)}`;
      }

      config.promiseArrKey = promiseArrKey;

      if (promiseArr[promiseArrKey]) {
        promiseArr[promiseArrKey]('Cancel operation');
        promiseArr[promiseArrKey] = cancel;
      } else {
        promiseArr[promiseArrKey] = cancel;
      }

      const token = getToken();
      if (token) {
        config.headers.Authorization = token; // 请求头传参token：Authorization
      }
      config.headers['req-agent'] = 'inside'; // 后端要求所有接口都传req-agent: inside
      config.headers['x-mse-tag'] = getMseTag();
      config.headers['application-code'] = '4'; // 通过解析出应用标识

      // 统一处理需要全局传参
      const account = getAccount();
      const METHOD = config.method.toLowerCase();
      const currentUserId = account ? account.id : '';
      const currentUserName = account ? account.userName : '';

      let keyName = 'params';
      if (['post', 'put'].includes(METHOD)) {
        keyName = 'data';
      }
      config[keyName].currentUserId = currentUserId;
      config[keyName].operator = currentUserName;

      // GET 请求设置时间戳参数, 解决缓存问题
      if (METHOD === 'get' && !config.cache) {
        config.params._ = Date.now();
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  // response interceptor
  instance.interceptors.response.use(
    async (response) => {
      delete promiseArr[response.config.promiseArrKey];
      hideFullScreenLoading();
      const { data, headers } = response;
      if (response.config.responseType.toLowerCase() === 'blob') {
        return Promise.resolve(response);
      }
      const { statusCode = '', success, code, message } = data;
      if (statusCode === 1 || success) {
        // 每次返回新的 token, 存起来, 用于新的会话过期判断
        const token = headers['h-auth-token'];
        if (token) {
          setToken(token);
        }
        return Promise.resolve(data);
      }

      // 没有登录 跳转到登录页面
      if (jumpLoginCodeList.includes(code) || statusCode === 999) {
        Message({
          type: 'error',
          message: message || '会话过期， 请重新登陆!',
        });
        const isExpiration = true;
        await store.dispatch('account/logout', isExpiration);
        // redirect to login
        router.replace('/login');
        return Promise.reject(data);
      }

      // 用户无应用权限（包括用户无角色），跳到登录不会自动调用ciam
      if ([1001, '1001'].includes(code)) {
        removeAccount();
      }

      // 没有权限 跳转到404
      if (code === 403) {
        router.replace('/404');
        return Promise.reject(data);
      }
      // token异常，退出
      if (tokenErrCodeList.includes(code)) {
        // 关闭所有 Message 实例
        Message.closeAll();
        Message({
          type: 'error',
          message: data.message,
        });
        setTimeout(async () => {
          const isExpiration = true;
          await store.dispatch('account/logout', isExpiration);
          // redirect to login
          router.replace('/login');
        }, 1000);
        return Promise.reject(data);
      }

      Message({
        type: 'error',
        message,
      });
      return Promise.reject(data);
    },
    async (error) => {
      hideFullScreenLoading();

      if (error && error.response) {
        const messages = {
          400: '错误请求',
          401: '会话过期，请重新登录',
          403: '拒绝访问',
          404: '请求错误，未找到该资源',
          405: '请求方法未允许',
          408: '请求超时',
          500: '服务器端出错',
          501: '网络未实现',
          502: '网络错误',
          503: '服务不可用',
          504: '网络超时',
          505: 'http版本不支持该请求',
        };
        error.message = messages[error.response.status] || `连接错误${error.response.status}`;
      } else {
        error.message = error.message || '连接到服务器失败';
      }

      try {
        const { status, statusText } = error.response;
        // 登录过期
        if (status === 401) {
          // 关闭所有 Message 实例
          Message.closeAll();
          Message({
            type: 'error',
            message: error.message,
          });
          const isExpiration = true;
          await store.dispatch('account/logout', isExpiration);
          // redirect to login
          router.replace('/login');
          return Promise.reject(error);
        }

        // 非登录过期错误
        Message({
          type: 'error',
          message: `${status} - ${error.message}`,
        });

        return Promise.reject({
          status,
          statusText,
          message: error.message,
        });
      } catch (err) {
        return Promise.reject({
          status: 'canceled',
          statusText: 'Cancel operation',
          message: error.message,
        });
      }
    },
  );

  return instance(defaults);
};


/* eslint-disable object-curly-newline */
const http = {
  /**
   * GET 请求
   *
   * @param {string} [url=''] 地址
   * @param {*} [params={}] 查询参数
   * @param {*} [config={}] axios 配置
   * @param {boolean} [loading=false] 是否显示 loading
   * @param {string} [loadingMessage='加载中...'] loading 信息提示
   * @param {string} responseType
   * @returns
   */
  get({ server = 'base', url = '', params = {}, config = {}, loading = false, loadingMessage = '加载中...', responseType = 'json' }) {
    const mergeConfig = {
      method: 'GET',
      server,
      url,
      params,
      loading,
      loadingMessage,
      cancelToken: new CancelToken((c) => {
        cancel = c;
      }),
      // eslint-disable-next-line no-shadow
      paramsSerializer: (params) => {
        return qs.stringify(params);
      },
      responseType,
      ...config,
    };
    return request(mergeConfig);
  },

  /**
   * POST 请求
   *
   * @param {string} [url=''] 地址
   * @param {*} [params={}] 查询参数
   * @param {*} [data={}] 提交内容
   * @param {*} [config={}] axios 配置
   * @param {boolean} [loading=false] 是否显示 loading
   * @param {string} [loadingMessage='加载中...'] loading 信息提示
   * @param {string} responseType
   * @return {*}
   */
  post({ server = 'base', url = '', params = {}, data = {}, config = {}, loading = false, loadingMessage = '正在提交...', responseType = 'json' }) {
    const mergeConfig = {
      method: 'POST',
      server,
      url,
      params,
      data,
      loading,
      loadingMessage,
      cancelToken: new CancelToken((c) => {
        cancel = c;
      }),
      responseType,
      ...config,
    };
    return request(mergeConfig);
  },

  /**
   * DELETE 请求
   *
   * @param {string} [url=''] 地址
   * @param {*} [params={}] 查询参数
   * @param {*} [data={}] 提交内容
   * @param {*} [config={}] axios 配置
   * @param {boolean} [loading=false] 是否显示 loading
   * @param {string} [loadingMessage='加载中...'] loading 信息提示
   * @param {string} responseType
   * @return {*}
   */
  delete({ server = 'base', url = '', params = {}, data = {}, config = {}, loading = false, loadingMessage = '正在删除...', responseType = 'json' }) {
    const mergeConfig = {
      method: 'DELETE',
      server,
      url,
      params,
      data,
      loading,
      loadingMessage,
      cancelToken: new CancelToken((c) => {
        cancel = c;
      }),
      // eslint-disable-next-line no-shadow
      paramsSerializer: (params) => {
        return qs.stringify(params);
      },
      responseType,
      ...config,
    };
    return request(mergeConfig);
  },

  /**
   * PUT 请求
   *
   * @param {string} [url=''] 地址
   * @param {*} [params={}] 查询参数
   * @param {*} [data={}] 提交内容
   * @param {*} [config={}] axios 配置
   * @param {boolean} [loading=false] 是否显示 loading
   * @param {string} [loadingMessage='加载中...'] loading 信息提示
   * @param {string} responseType
   * @return {*}
   */
  put({ server = 'base', url = '', params = {}, data = {}, config = {}, loading = false, loadingMessage = '正在提交...', responseType = 'json' }) {
    const mergeConfig = {
      method: 'PUT',
      server,
      url,
      params,
      data,
      loading,
      loadingMessage,
      cancelToken: new CancelToken((c) => {
        cancel = c;
      }),
      responseType,
      ...config,
    };
    return request(mergeConfig);
  },

  /**
   * PATCH 请求
   *
   * @param {string} [url=''] 地址
   * @param {*} [params={}] 查询参数
   * @param {*} [data={}] 提交内容
   * @param {*} [config={}] axios 配置
   * @param {boolean} [loading=false] 是否显示 loading
   * @param {string} [loadingMessage='加载中...'] loading 信息提示
   * @param {string} responseType
   * @return {*}
   */
  patch({ server = 'base', url = '', params = {}, data = {}, config = {}, loading = false, loadingMessage = '正在提交...', responseType = 'json' }) {
    const mergeConfig = {
      method: 'PATCH',
      server,
      url,
      params,
      data,
      loading,
      loadingMessage,
      cancelToken: new CancelToken((c) => {
        cancel = c;
      }),
      responseType,
      ...config,
    };
    return request(mergeConfig);
  },

  /**
   * 并发请求
   *
   * @param {*} [args=[]] 请求数组
   * @returns
   */
  all: (args = []) => axios.all(args),
};

export default http;
